import React from "react";
import Table from 'react-bootstrap/Table'
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal'
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useSearchParams  } from "react-router-dom";
import VehicleDetails from "./VehicleDetails/VehicleDetails";
import VehicleTitle from "./VehicleDetails/VehicleTitle";
import { useInventoryContext } from "./InventoryContext";
import { useVehicleContext } from "./VehicleContext";
import { mergeVehicles } from "../../lib/mergeVehicles.js";
import "./ModalZ.css";

const calcAge = (dt) => {
  if (dt === null)
    return null;
  return Math.floor((new Date().getTime() - new Date(dt).getTime()) / (1000 * 3600 * 24));
}

const resizeUrl = (url, width) => {
  if (!url) return null;
  if (/https:\/\/assets\.cai-media-management\.com\/(resize\/1024x1024\/)?common-vehicle-media\/(.*)/im.test(url)) {
    return url.replace(/https:\/\/assets\.cai-media-management\.com\/(resize\/.*?\/)?common-vehicle-media\/(.*)/img, "https://assets.cai-media-management.com/resize/100x0/common-vehicle-media/$2");
  } else if (/http:\/\/vehicle-photos-published\.vauto\.com\/(..\/..\/..\/.*?\/.*?)\.jpg/im.test(url)) {
    return url.replace(/http:\/\/vehicle-photos-published\.vauto\.com\/(..\/..\/..\/.*?\/.*?)\.jpg/img, "https://vehicle-photos-published-primary.s3.amazonaws.com/$1-thumb.jpg");
  } else {
    return url;
  }
}

function CombinedVehicleList(props) {
  const inventoryContext = useInventoryContext();
  const vehicleContext = useVehicleContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicleSet, setVehicleSet] = React.useState("all");
  const [loadedVehicle, setLoadedVehicle] = React.useState(null);
  const [baseTitle, setBaseTitle] = React.useState(document.title);

  const { selectedTab, setSelectedTab } = props;

  const selectedVin = searchParams.get("vin");
  const setSelectedVin = (vin) => {
    if (vin === selectedVin)
      return;
    setSearchParams((prev) => {
      if (vin) {
        prev.set("vin", vin);
      } else {
        prev.delete("vin");
        prev.delete("tab");
      }
      return prev;
    });
  }
    
  const _loadVehicle = async (v) => {
    const getId = (v, key) => { return v.assoc[key] && v.assoc[key].length > 0 ? v.assoc[key][0].id : null; };
    const getRaw = (v, key, name) => { return v.assoc[key] && v.assoc[key].length > 0 ? v.assoc[key][0].raw[name] : null; };
    
    //console.log(v);
    vehicleContext.loadVehicle({
      vin: v.vin,
      cviVehicleId: getId(v, "cvi"),
      // CVI 2.1 details come from the unprojected read by inventory record id; the list row's
      // projection is only what the table needs. `raw` is for sources with no read by id.
      cvi2VehicleId: getId(v, "cvi2"),
      vaVehicleId: getId(v, "va"),
      mvsVehicleId: getId(v, "mvs"),
      iolVehicleId: getId(v, "iol"),
      ddcVehicleId: getId(v, "ddc"),
      vaSrcVehicle: getRaw(v, "vasrc", "Source"),
      vinVehicle: getRaw(v, "vinsol", "Vin"),
      atcVehicleId: getId(v, "atc")
    });
    setLoadedVehicle(v);
  }

  let mergedVehicles = mergeVehicles([
    {key: "cvi", vehicles: inventoryContext.cviSvsInventory.vehicles},
    {key: "cvi2", vehicles: inventoryContext.cvi2Inventory.vehicles},
    {key: "va", vehicles: inventoryContext.vaCoreInventory.vehicles},
    {key: "mvs", vehicles: inventoryContext.vaMvsInventory.vehicles},
    {key: "iol", vehicles: inventoryContext.iolInventory.vehicles},
    {key: "vasrc", vehicles: inventoryContext.cviVaSrcInventory.vehicles},
    {key: "ddc", vehicles: inventoryContext.ddcInventory.vehicles},
    {key: "vinsol", vehicles: inventoryContext.vinInventory.vehicles},
    {key: "atc", vehicles: inventoryContext.atcInventory.vehicles}
  ]);

  let vehicles = mergedVehicles.all;
  switch (vehicleSet) {
    case "all":
      vehicles = mergedVehicles.all;
      break;
    case "cvi": case "cvi2": case "va": case "vasrc": case "mvs": case "iol": case "ddc": case "vinsol": case "atc":
      vehicles = mergedVehicles.filtered[vehicleSet];
      break;
    case "cviNotVa":
      vehicles = mergedVehicles.cviNotVa;
      break;
    case "vaNotCvi":
      vehicles = mergedVehicles.vaNotCvi;
      break;
    case "multiMatch":
      vehicles = mergedVehicles.multiMatch;
      break;
    case "vinNotCvi":
      vehicles = mergedVehicles.vinNotCvi;
      break;
    case "vinNotDdc":
      vehicles = mergedVehicles.vinNotDdc;
      break;
    default:
  }
  if (!vehicles) {
    vehicles = [];
  }

  React.useEffect(() => {
    const loadedVin = loadedVehicle ? loadedVehicle.vin : null;
    //console.log("Load?", selectedVin, loadedVehicle ? loadedVehicle.vin : null, inventoryContext.isReady);
    if (selectedVin && selectedVin !== loadedVin && inventoryContext.isReady) {
       let v = vehicles.find((v) => v.vin === selectedVin);
      if (v) {
        setBaseTitle(document.title);
        document.title = v.vin;
        _loadVehicle(v);
      }
    } else if (!selectedVin && loadedVehicle) {
      document.title = baseTitle;
      setLoadedVehicle(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryContext.isReady, selectedVin, loadedVehicle]);

  function StatusButton(props) {
    let inventory = props.inventory;
    let label = props.label;
    let targetVehicleSet = props.vehicleSet;
    let id = props.id;

    switch (inventory.status) {
      case "LOADING":
        return <Button id={id} variant={targetVehicleSet === vehicleSet ? "success" : "outline-success"} onClick={() => setVehicleSet(targetVehicleSet)}>{label} - <Spinner animation="border" size="sm" /></Button>
      case "READY":
        return <Button id={id} variant={targetVehicleSet === vehicleSet ? "success" : "outline-success"} onClick={() => setVehicleSet(targetVehicleSet)}>{label} - <b>{inventory.vehicles.length}</b></Button>
      case "ERROR":
        return <Button id={id} variant={targetVehicleSet === vehicleSet ? "success" : "outline-danger"} onClick={() => setVehicleSet(targetVehicleSet)}>{label} -<b>Error loading</b></Button>
      case "NONE":
        return <></>;
      default:
        throw new Error("Unknown status: " + inventory.status);
    }
  }

  return <div>
    <Container fluid>
      <Card>
        <Tabs activeKey={selectedTab} onSelect={(key) => setSelectedTab(key)} id="inventory-tabs">
          <Tab eventKey="1" title="Summary View">
            {/* Content for Summary View */}
          </Tab>
          <Tab eventKey="2" title="Comparison View">
            {/* Content for Comparison View */}
          </Tab>
        </Tabs>
        <Card.Body className="inventory-results">
        {(inventoryContext.isLoading) ? <div>
            <Button id="all-button" variant={vehicleSet === "all" ? "success" : "outline-success"} onClick={() => setVehicleSet("all")}>All - {mergedVehicles.all ? <b>{mergedVehicles.all.length}</b> : <Spinner animation="border" size="sm" />}</Button>
            {/* NOTE: Pendo thinks "cvi" is autogenerated and skips it for suggested selection, so we use "cavi" instead in all our IDs. */}
            <StatusButton id="cavi-button" label="CVI 1.0" vehicleSet="cvi" inventory={inventoryContext.cviSvsInventory} />
            <StatusButton id="cavi2-button" label="CVI 2.1" vehicleSet="cvi2" inventory={inventoryContext.cvi2Inventory} />
            <StatusButton id="va-button" label="vAuto" vehicleSet="va" inventory={inventoryContext.vaCoreInventory} />
            <StatusButton id="mvs-button" label="Merch" vehicleSet="mvs" inventory={inventoryContext.vaMvsInventory} />
            <StatusButton id="iol-button" label="IOL" vehicleSet="iol" inventory={inventoryContext.iolInventory} />
            <StatusButton id="ddc-button" label="DDC" vehicleSet="ddc" inventory={inventoryContext.ddcInventory} />
            <StatusButton id="vin-button" label="VIN" vehicleSet="vinsol" inventory={inventoryContext.vinInventory} />
            <StatusButton id="atc-button" label="ATC" vehicleSet="atc" inventory={inventoryContext.atcInventory} />
            <StatusButton id="vasrc-button" label="vA Source" vehicleSet="vasrc" inventory={inventoryContext.cviVaSrcInventory} />
 
            {(inventoryContext.cviSvsInventory.status === "READY" && inventoryContext.vaCoreInventory.status === "READY") && <>
            <Button id="vaNotCavi-button" disabled={mergedVehicles.vaNotCvi && mergedVehicles.vaNotCvi.length === 0} variant={vehicleSet === "vaNotCvi" ? "danger" : "outline-danger"} onClick={() => setVehicleSet("vaNotCvi")}>vAuto Vehicles NOT in CVI - {mergedVehicles.vaNotCvi ? <b>{mergedVehicles.vaNotCvi.length}</b> : <Spinner animation="border" size="sm" />}</Button>
            <Button id="caviNotVa-button" disabled={mergedVehicles.cviNotVa && mergedVehicles.cviNotVa.length === 0} variant={vehicleSet === "cviNotVa" ? "warning" : "outline-warning"} onClick={() => setVehicleSet("cviNotVa")}>CVI Vehicles NOT in vAuto - {mergedVehicles.cviNotVa ? <b>{mergedVehicles.cviNotVa.length}</b> : <Spinner animation="border" size="sm" />}</Button>
            </>}
            {(inventoryContext.vinInventory.status === "READY" && inventoryContext.cviSvsInventory.status === "READY") && <>
            <Button id="vinNotCavi-button" disabled={mergedVehicles.vinNotCvi && mergedVehicles.vinNotCvi.length === 0} variant={vehicleSet === "vinNotCvi" ? "danger" : "outline-danger"} onClick={() => setVehicleSet("vinNotCvi")}>VIN Vehicles NOT in CVI - {mergedVehicles.vinNotCvi ? <b>{mergedVehicles.vinNotCvi.length}</b> : <Spinner animation="border" size="sm" />}</Button>
            </>}
            {(inventoryContext.vinInventory.status === "READY" && inventoryContext.ddcInventory.status === "READY") && <>
            <Button id="vinNotDdc-button" disabled={mergedVehicles.vinNotDdc && mergedVehicles.vinNotDdc.length === 0} variant={vehicleSet === "vinNotDdc" ? "danger" : "outline-danger"} onClick={() => setVehicleSet("vinNotDdc")}>VIN Vehicles NOT in DDC - {mergedVehicles.vinNotDdc ? <b>{mergedVehicles.vinNotDdc.length}</b> : <Spinner animation="border" size="sm" />}</Button>
            </>}
            <Button id="multiMatch-button" disabled={mergedVehicles.multiMatch && mergedVehicles.multiMatch.length === 0} variant={vehicleSet === "multiMatch" ? "warning" : "outline-warning"} onClick={() => setVehicleSet("multiMatch")}>Vehicles with Multiple matches - {mergedVehicles.multiMatch ? <b>{mergedVehicles.multiMatch.length}</b> : <Spinner animation="border" size="sm" />}</Button>
          </div> : <div>
          <Button disabled={true} variant="secondary">No data loaded</Button>
          </div> }
        </Card.Body>
      </Card>
    </Container>

    {inventoryContext.isReady ? 
    <Table striped bordered hover className="sticky-header-table">
      <thead>
      <tr>
        <th width="1"></th>
        <th width="1"></th>
        <th>VIN</th>
        <th>Stock#</th>
        <th>Type</th>
        <th>Year / Make / Model / Trim</th>
        <th>Color</th>
        <th>Age</th>
        <th>Pur. Status</th>
      </tr>        
      </thead>
      <tbody>
        {vehicles.map((v) =>
        <tr key={v.vin} onClick={() => setSelectedVin(v.vin)} style={{cursor: 'pointer'}}>
          <td style={{textAlign: "center"}}>{v.error ? <img src="warning.png" height="20" title={v.error} alt={v.error} /> : ""}</td>
          <td>{v.mediaUrl && <img src={resizeUrl(v.mediaUrl, 32)} height="32" alt="[IMG]" />}</td>
          <td>{v.vin}</td>
          <td>{v.stock}</td>
          <td>{v.type}</td>
          <td>{v.year} {v.make} {v.model} {v.trim}</td>
          <td>{v.exteriorColor}</td>
          <td>{calcAge(v.dateInStock)}</td>
          <td style={{color: v.status === "MISMATCH" ? "red" : "grey"}}>{v.status}</td>
        </tr>)}
      </tbody>
    </Table>: <div></div>}
          {loadedVehicle != null &&
    <Modal centered fullscreen show={loadedVehicle} onHide={() => setSelectedVin(null)}>
      <Modal.Header closeButton>
        <Modal.Title><VehicleTitle /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <VehicleDetails />
      </Modal.Body>
      <Modal.Footer>
        <Button id="close-modal-button" variant="secondary" onClick={() => setSelectedVin(null)}>Close</Button>
      </Modal.Footer>
    </Modal>}

    </div>;
}

export default CombinedVehicleList;
